/**
 * CalendarPage - calendário mensal de publicações com drag-to-reschedule (dnd-kit)
 */
import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { PT_DAYS_SHORT, PT_MONTHS } from "@/lib/date";
import ChannelIcon from "@/components/shared/ChannelIcon";
import StatusBadge from "@/components/shared/StatusBadge";
import { Link } from "react-router-dom";
import {
    DndContext,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

function Day({ date, posts, isCurrentMonth, onItemClick }) {
    const id = date.toISOString().slice(0, 10);
    const { setNodeRef, isOver } = useDroppable({ id });
    const today =
        date.toDateString() === new Date().toDateString();
    return (
        <div
            ref={setNodeRef}
            className={`min-h-[110px] sm:min-h-[120px] p-2 rounded-xl border ${
                isOver
                    ? "border-[#6B21D9] bg-[#6B21D9]/10"
                    : "border-white/5 bg-card/30"
            } ${!isCurrentMonth ? "opacity-40" : ""}`}
            data-testid={`calendar-day-${id}`}
        >
            <div
                className={`text-xs font-medium mb-1 ${
                    today ? "text-[#A78BFA]" : "text-muted-foreground"
                }`}
            >
                {date.getDate()}
                {today && <span className="ml-1 text-[10px] text-[#A78BFA]">hoje</span>}
            </div>
            <div className="space-y-1">
                {posts.map((p) => (
                    <DraggablePost key={p.id} post={p} onClick={() => onItemClick(p)} />
                ))}
            </div>
        </div>
    );
}

function DraggablePost({ post, onClick }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: post.id,
    });
    const c = post.channels?.[0] || "instagram";
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    ref={setNodeRef}
                    {...listeners}
                    {...attributes}
                    onClick={onClick}
                    className={`w-full text-left text-[11px] px-2 py-1.5 rounded-lg cursor-grab ${
                        post.status === "publicado"
                            ? "bg-[#2DD4BF]/15 text-[#2DD4BF] border border-[#2DD4BF]/25"
                            : "bg-[#6B21D9]/15 text-[#A78BFA] border border-[#6B21D9]/30"
                    } ${isDragging ? "opacity-60" : ""} hover:opacity-90`}
                    data-testid={`calendar-post-${post.id}"`}
                >
                    <div className="flex items-center gap-1">
                        <ChannelIcon type={c} className="h-3 w-3" />
                        <span className="truncate flex-1">{post.caption || "Publicação"}</span>
                    </div>
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-72">
                <div className="space-y-2">
                    <StatusBadge status={post.status} />
                    <p className="text-sm line-clamp-3">{post.caption}</p>
                    {post.metrics && (
                        <div className="text-xs text-muted-foreground">
                            👁️ {post.metrics.reach} · ❤️ {post.metrics.likes}
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default function CalendarPage() {
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth());
    const [posts, setPosts] = useState([]);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const load = () => {
        api.get(`/calendar?year=${year}&month=${month + 1}`).then((r) => setPosts(r.data));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [year, month]);

    const days = useMemo(() => {
        const firstOfMonth = new Date(year, month, 1);
        const startDay = firstOfMonth.getDay();
        const lastDay = new Date(year, month + 1, 0).getDate();
        const cells = [];
        for (let i = startDay - 1; i >= 0; i--) {
            const d = new Date(year, month, -i);
            cells.push({ date: d, isCurrentMonth: false });
        }
        for (let i = 1; i <= lastDay; i++) {
            cells.push({ date: new Date(year, month, i), isCurrentMonth: true });
        }
        const tail = (7 - (cells.length % 7)) % 7;
        for (let i = 1; i <= tail; i++) {
            cells.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
        }
        return cells;
    }, [year, month]);

    const postsByDay = useMemo(() => {
        const map = {};
        for (const p of posts) {
            const ref = p.scheduled_for || p.published_at;
            if (!ref) continue;
            const key = new Date(ref).toISOString().slice(0, 10);
            (map[key] ||= []).push(p);
        }
        return map;
    }, [posts]);

    const navigate = (delta) => {
        const m = month + delta;
        if (m < 0) { setYear(year - 1); setMonth(11); }
        else if (m > 11) { setYear(year + 1); setMonth(0); }
        else setMonth(m);
    };

    const handleDragEnd = async (e) => {
        const { active, over } = e;
        if (!over) return;
        const post = posts.find((p) => p.id === active.id);
        if (!post) return;
        const oldRef = post.scheduled_for || post.published_at;
        const oldDate = new Date(oldRef);
        const newDate = new Date(over.id);
        newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), 0, 0);
        if (newDate.toISOString() === oldDate.toISOString()) return;
        try {
            await api.put(`/posts/${post.id}`, {
                scheduled_for: newDate.toISOString(),
                status: post.status === "publicado" ? "publicado" : "agendado",
            });
            toast.success("Publicação reagendada");
            load();
        } catch (err) {
            toast.error(getApiErrorMessage(err));
        }
    };

    return (
        <div className="space-y-5" data-testid="calendar-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                        Calendário de Post
                    </h1>
                    <p className="text-muted-foreground">Arraste para reagendar.</p>
                </div>
                <Button asChild className="btn-grad-purple text-white">
                    <Link to="/publicacoes?new=1" className="gap-2">
                        <Plus className="h-4 w-4" /> Nova publicação
                    </Link>
                </Button>
            </div>

            <Card className="glass-card p-4 sm:p-5 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="calendar-prev-month-button">
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <h2 className="font-display font-semibold text-lg w-44 text-center" data-testid="calendar-current-label">
                            {PT_MONTHS[month]} {year}
                        </h2>
                        <Button variant="outline" size="icon" onClick={() => navigate(1)} data-testid="calendar-next-month-button">
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => {
                            setYear(today.getFullYear());
                            setMonth(today.getMonth());
                        }}
                    >
                        Hoje
                    </Button>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-2">
                    {PT_DAYS_SHORT.map((d) => (
                        <div key={d} className="text-center text-[10px] uppercase font-semibold text-muted-foreground">
                            {d}
                        </div>
                    ))}
                </div>
                <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                    <div className="grid grid-cols-7 gap-1">
                        {days.map((d) => {
                            const key = d.date.toISOString().slice(0, 10);
                            return (
                                <Day
                                    key={key}
                                    date={d.date}
                                    isCurrentMonth={d.isCurrentMonth}
                                    posts={postsByDay[key] || []}
                                    onItemClick={() => {}}
                                />
                            );
                        })}
                    </div>
                </DndContext>
            </Card>
        </div>
    );
}
